import { KatapultGeometry } from 'katapult-toolbox';

/**
 * Snap a location to the closest point along a connection path, including every bend between its end nodes.
 * @param {number} latitude
 * @param {number} longitude
 * @param {[number, number][]} pathPoints The connection's points in order as latitude and longitude pairs.
 * @returns {{latitude: number, longitude: number, distanceInMeters: number}} The snapped location and how far it moved, or the unchanged location at an infinite distance when the path has no segment.
 */
export function SnapLocationToConnectionPath(latitude, longitude, pathPoints) {
  // Leave the location where it is when the path has no segment to snap onto
  const hasSegmentToSnapOnto = pathPoints?.length >= 2;
  if (!hasSegmentToSnapOnto) return { latitude, longitude, distanceInMeters: Infinity };

  // Project the location and path into the same flat grid so distances are in meters
  const projectedLocation = KatapultGeometry.LatLongToXY(latitude, longitude);
  const projectedPathPoints = pathPoints.map(([pointLatitude, pointLongitude]) =>
    KatapultGeometry.LatLongToXY(pointLatitude, pointLongitude, projectedLocation.srid)
  );

  // Find the closest point on each segment and keep the one nearest the location
  let closestProjectedPoint = null;
  let closestDistanceInMeters = Infinity;
  for (let pointIndex = 1; pointIndex < projectedPathPoints.length; pointIndex++) {
    const segmentStart = projectedPathPoints[pointIndex - 1];
    const projectedPointOnSegment = KatapultGeometry.SnapToLineXY(projectedLocation, segmentStart, projectedPathPoints[pointIndex]);
    const distanceInMeters = Math.hypot(projectedPointOnSegment.dx, projectedPointOnSegment.dy);
    if (distanceInMeters < closestDistanceInMeters) {
      closestProjectedPoint = projectedPointOnSegment;
      closestDistanceInMeters = distanceInMeters;
    }
  }

  // Convert the closest point back to latitude and longitude
  const closestLocation = KatapultGeometry.XyToLatLong({
    ...closestProjectedPoint,
    srid: projectedLocation.srid
  });
  return {
    latitude: closestLocation.lat,
    longitude: closestLocation.long,
    distanceInMeters: closestDistanceInMeters
  };
}
